import { match } from 'ts-pattern'

import { ConditionalFunctionString } from '../../conditions.types'
import type { SemanticType } from '../../../semanticTypes'
import { sameAssetIds, samePaymentTerms, sameRawText } from '../runtimeHelpers'
import {
  isQuoteComparisonSubOperator,
  pricingOutcomeExpression,
  readPricingComparison,
  readQuoteComparisonValue,
  type QuoteComparisonSubOperator,
  type QuoteComparisonValue,
  type QuotePricingComparison
} from './quoteComparison'
import { scopedSemanticTypeRuntimePath } from './utils'

/** The `previous_quote_approval` outcome a row counts as a prior sign-off. */
const APPROVED_OUTCOME = 'APPROVED'

/**
 * What a PREVIOUS_APPROVAL row stores. `reviewNodeId` picks the review step the
 * approval must have come from — and doubles as the scope for the supporting
 * task's runtime keys, since each step has its own approvers.
 */
export type PreviousApprovalConditionValue = QuoteComparisonValue & {
  reviewNodeId?: string
}

export const readPreviousApprovalValue = (
  value: unknown
): PreviousApprovalConditionValue =>
  readQuoteComparisonValue(value) as PreviousApprovalConditionValue

/**
 * Configuration for the previously-approved-quote comparison factories. Both
 * semantic types are published by the property's `valueSupportingTask` — the
 * approved quote's side of the comparison, and the outcome every row gates on.
 */
export type PreviousApprovalFunctionStringConfig = {
  approvedSemanticType: SemanticType
  outcomeSemanticType: SemanticType
}

/**
 * Pricing is compared by an executor rather than in the expression, so its
 * factory takes the semantic type of each outcome the executor publishes — one
 * per pricing comparison the row can choose.
 */
export type PreviousApprovalPricingFunctionStringConfig = {
  comparisonSemanticTypes: Record<QuotePricingComparison, SemanticType>
  outcomeSemanticType: SemanticType
}

/**
 * Factory for PREVIOUS_APPROVAL.ATTACHMENTS. Compares the quote's attachments
 * against those of its last approved version, by set of asset ids.
 */
export const previousApprovalAttachments = (
  config: PreviousApprovalFunctionStringConfig
): ConditionalFunctionString =>
  previousApprovalComparison({ compare: sameAssetIds, config })

/**
 * Factory for PREVIOUS_APPROVAL.RAW_TEXT. Compares the quote's standard text
 * against that of its last approved version, as text.
 */
export const previousApprovalRawText = (
  config: PreviousApprovalFunctionStringConfig
): ConditionalFunctionString =>
  previousApprovalComparison({ compare: sameRawText, config })

/**
 * Factory for PREVIOUS_APPROVAL.PAYMENT_TERMS. Compares the quote's payment
 * terms against those of its last approved version, as a day count.
 */
export const previousApprovalPaymentTerms = (
  config: PreviousApprovalFunctionStringConfig
): ConditionalFunctionString =>
  previousApprovalComparison({ compare: samePaymentTerms, config })

/**
 * Factory for PREVIOUS_APPROVAL.PRICING. Reads the outcome
 * `compare_quote_pricing` published for the chosen review step and pricing
 * comparison.
 *
 * An unconfigured row compares as `false`, as the other fields do.
 */
export const previousApprovalPricing =
  (
    config: PreviousApprovalPricingFunctionStringConfig
  ): ConditionalFunctionString =>
  ({ value }) => {
    const { reviewNodeId, operator } = readPreviousApprovalValue(value)
    if (!reviewNodeId) return 'false'
    if (!isQuoteComparisonSubOperator(operator)) return 'false'

    return whenPreviouslyApproved({
      outcomeSemanticType: config.outcomeSemanticType,
      reviewNodeId,
      comparison: pricingOutcomeExpression({
        semanticType:
          config.comparisonSemanticTypes[readPricingComparison(value)],
        scope: reviewNodeId,
        subOperator: operator
      })
    })
  }

/**
 * Builds a previous-approval comparison from an equality runtime helper. Each
 * field supplies only that helper — IS_NOT_EQUAL is its negation.
 *
 *   compare( property                   , scoped approval path      )
 *          | quote side, the operator's | published by the row's
 *          | valueSemanticType          | valueSupportingTask
 */
const previousApprovalComparison =
  ({
    compare,
    config
  }: {
    compare: (quoteValue: string, approvedValue: string) => string
    config: PreviousApprovalFunctionStringConfig
  }): ConditionalFunctionString =>
  ({ property, value }) => {
    const { reviewNodeId, operator } = readPreviousApprovalValue(value)
    if (!reviewNodeId) return 'false'
    if (!isQuoteComparisonSubOperator(operator)) return 'false'

    const equal = compare(
      property,
      scopedSemanticTypeRuntimePath({
        semanticType: config.approvedSemanticType,
        scope: reviewNodeId
      })
    )

    return whenPreviouslyApproved({
      outcomeSemanticType: config.outcomeSemanticType,
      reviewNodeId,
      comparison: negateFor({ subOperator: operator, equal })
    })
  }

const negateFor = ({
  subOperator,
  equal
}: {
  subOperator: QuoteComparisonSubOperator
  equal: string
}): string =>
  match(subOperator)
    .with('IS_EQUAL', () => equal)
    .with('IS_NOT_EQUAL', () => `!(${equal})`)
    .exhaustive()

/**
 * Gates a comparison on the chosen review step having approved this quote. The
 * gate wraps the comparison rather than sitting inside it, so "unknown" never
 * reads as "changed":
 *
 *   no approval  -> outcome absent    -> false, both sub-operators
 *   rejected     -> outcome REJECTED  -> false, both sub-operators
 *   approved     -> the comparison decides
 */
const whenPreviouslyApproved = ({
  outcomeSemanticType,
  reviewNodeId,
  comparison
}: {
  outcomeSemanticType: SemanticType
  reviewNodeId: string
  comparison: string
}): string => {
  const outcome = scopedSemanticTypeRuntimePath({
    semanticType: outcomeSemanticType,
    scope: reviewNodeId
  })

  return `(${outcome} === ${JSON.stringify(APPROVED_OUTCOME)}) && (${comparison})`
}
